import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Per-route SEO. Pages that already set their own title (courier pages, About)
// are intentionally omitted so they self-manage; this still sets canonical for them.
const BRAND = 'SendMyParcel';
const ORIGIN = 'https://sendmyparcel.in';

// Routes that prerender writes as their own directory (see scripts/prerender.js).
// The server serves build/<route>/index.html at "/<route>/" and 301s the
// slash-less form to it, so the canonical has to carry the slash. Without it the
// page declares a canonical that redirects, and Google files the URL under
// "Page with redirect" and declines to index it.
const DIRECTORY_ROUTES = new Set([
  '/support',
  '/about',
  '/quote',
  '/track',
  '/privacy',
  '/privacy-policy',
  '/terms-and-conditions',
  '/send-courier-from-india-to-usa',
  '/send-courier-from-india-to-uk',
  '/send-courier-from-india-to-europe',
  '/send-courier-from-india-to-canada',
  '/send-courier-from-india-to-australia',
]);

// /privacy and /privacy-policy render the same document. Rather than let Google
// choose between two URLs for one policy, /privacy names the other as canonical
// and only /privacy-policy/ is submitted in the sitemap.
const CANONICAL_OVERRIDES = { '/privacy': '/privacy-policy/' };

// Routes the app actually serves. Anything else fell through to the catch-all
// 404 route, and an error page must not invite indexing or declare itself the
// canonical version of whatever URL produced it.
const KNOWN_ROUTES = new Set([
  '/', '/quote', '/login', '/reset-password', '/track', '/support', '/about',
  '/privacy', '/privacy-policy', '/terms-and-conditions', '/dashboard',
  '/profile', '/my-shipments', '/wallet',
  '/send-courier-from-india-to-usa',
  '/send-courier-from-india-to-uk',
  '/send-courier-from-india-to-europe',
  '/send-courier-from-india-to-canada',
  '/send-courier-from-india-to-australia',
]);
const KNOWN_PREFIXES = ['/admin', '/profile'];

// "/quote/" and "/quote" are one route: nginx serves the directory form.
const routeKey = (pathname) =>
  (pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname);

const isKnownRoute = (pathname) => {
  const p = routeKey(pathname);
  return KNOWN_ROUTES.has(p) || KNOWN_PREFIXES.some((k) => p === k || p.startsWith(k + '/'));
};

const canonicalUrl = (pathname) => {
  const p = routeKey(pathname);
  return ORIGIN + (CANONICAL_OVERRIDES[p] || (DIRECTORY_ROUTES.has(p) ? p + '/' : p));
};

const SEO = {
  '/': { title: 'SendMyParcel India: International Courier from India', desc: 'International courier services from India to 190+ countries. Compare DHL, FedEx, UPS, India Post and SendMyParcel rates, book online and track every parcel.' },
  '/quote': { title: `Get an International Shipping Quote | ${BRAND}`, desc: 'Get instant international courier quotes from India. Compare DHL, FedEx, UPS, India Post and SendMyParcel prices, then book in minutes.' },
  '/track': { title: `Track Your Shipment | ${BRAND}`, desc: 'Track your international courier and parcel in real time with SendMyParcel.' },
  '/support': { title: `Customer Support & Help | ${BRAND}`, desc: 'Get help with shipping, tracking, KYC and payments. Contact SendMyParcel support.' },
  '/privacy': { title: `Privacy Policy | ${BRAND}`, desc: 'How SendMyParcel collects, uses, stores and protects your personal data when you ship with us and use our website.' },
  '/privacy-policy': { title: `Privacy Policy | ${BRAND}`, desc: 'How SendMyParcel collects, uses, stores and protects your personal data when you ship with us and use our website.' },
  '/terms-and-conditions': { title: `Terms & Conditions | ${BRAND}`, desc: 'The terms that apply when you book and ship international parcels with SendMyParcel and when you use our website.' },
  '/dashboard': { title: `My Dashboard | ${BRAND}`, desc: 'Manage your shipments, wallet and profile.' },
  '/my-shipments': { title: `My Shipments | ${BRAND}`, desc: 'View and manage your shipments.' },
  '/wallet': { title: `My Wallet | ${BRAND}`, desc: 'Manage your wallet balance and transactions.' },
  '/profile': { title: `Profile & Settings | ${BRAND}`, desc: 'Manage your account details and KYC.' },
};

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const removeCanonical = () => {
  const el = document.querySelector('link[rel="canonical"]');
  if (el && el.parentNode) el.parentNode.removeChild(el);
};

const setProp = (property, content) => {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const SeoManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!isKnownRoute(pathname)) {
      // The catch-all 404 route. The shell ships an indexable robots tag, so an
      // unknown URL used to be an invitation to index an error page - and a
      // self-canonical made it the preferred version of itself.
      setMeta('robots', 'noindex, follow');
      removeCanonical();
      document.title = `Page Not Found | ${BRAND}`;
      return;
    }
    setMeta('robots', 'index, follow, max-image-preview:large');
    const url = canonicalUrl(pathname);
    setCanonical(url);
    setProp('og:url', url);
    const entry = SEO[routeKey(pathname)];
    if (entry) {
      document.title = entry.title;
      setMeta('description', entry.desc);
      setProp('og:title', entry.title);
      setProp('og:description', entry.desc);
      setMeta('twitter:title', entry.title);
      setMeta('twitter:description', entry.desc);
    }
  }, [pathname]);
  return null;
};

export default SeoManager;
